import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useNF } from "@/context/NFContext";
import { useNFDetalhe } from "@/context/useAtualizacaoNF";
import { useAuth } from "@/context/AuthContext";
import { atualizarContadoresSidebar } from "@/hooks/useContadoresSidebar";
import { ReprocessarNotaButton, PODE_REPROCESSAR } from "@/components/ReprocessarNotaButton";
import {
  ArquivoNF,
  CabecalhoNF,
  CamposNF,
  EstadoDetalheNF,
  HistoricoNF,
  LayoutDetalheNF,
  MotivoRecusaNF,
} from "@/components/nf/DetalheNFPartes";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const PODE_ANALISAR = ["Em análise", "Válido"];
const PODE_APROVAR_MANUAL = ["Pendente", "Erro OCR", "Recusada", "Com ressalvas"];
const NAO_PODE_RECUSAR = ["Recusada", "Cancelada"];

const VOLTAR = { to: "/rh/notas", label: "Notas Fiscais" };

const RHDetalheNF = () => {
  const { id } = useParams();
  const { aprovar, aprovarManual, recusar } = useNF();
  const { nf, carregando, atualizar } = useNFDetalhe(id);
  const { user } = useAuth();
  // Financeiro vê o detalhe, mas só o RH aprova/recusa.
  const podeAnalisar = user?.role === "rh";

  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAprovar = async () => {
    if (!nf) return;
    setLoading(true);
    try {
      atualizar(await aprovar(nf.id));
      atualizarContadoresSidebar();
      toast.success("NF aprovada com sucesso");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao aprovar");
    } finally {
      setLoading(false);
    }
  };

  const handleAprovarManual = async () => {
    if (!nf) return;
    setLoading(true);
    try {
      atualizar(await aprovarManual(nf.id));
      atualizarContadoresSidebar();
      toast.success("NF aprovada manualmente com sucesso");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao aprovar manualmente");
    } finally {
      setLoading(false);
    }
  };

  const handleRecusar = async () => {
    if (!nf) return;
    if (motivo.trim().length < 5) {
      toast.error("Informe um motivo (mín. 5 caracteres)");
      return;
    }
    setLoading(true);
    try {
      atualizar(await recusar(nf.id, motivo.trim()));
      atualizarContadoresSidebar();
      toast.success("NF recusada");
      setMotivo("");
      setRecusando(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao recusar");
    } finally {
      setLoading(false);
    }
  };

  const cancelarRecusa = () => {
    setRecusando(false);
    setMotivo("");
  };

  const podeRecusar = !!nf && podeAnalisar && !NAO_PODE_RECUSAR.includes(nf.status);
  const podeAprovar = !!nf && podeAnalisar && PODE_ANALISAR.includes(nf.status);
  const podeAprovarManual = !!nf && podeAnalisar && PODE_APROVAR_MANUAL.includes(nf.status);
  // Mesmas regras do ReprocessarNotaButton (que some quando não pode), para não sobrar card vazio.
  const podeReprocessar =
    !!nf &&
    PODE_REPROCESSAR.includes(nf.status) &&
    (podeAnalisar || nf.colaboradorId === user?.id) &&
    (nf.status !== "Aprovada" || podeAnalisar);
  const temAcoes = podeRecusar || podeAprovar || podeAprovarManual || podeReprocessar;

  return (
    <RoleGuard role={["rh", "financeiro"]}>
      <AppLayout title="Detalhe da NF">
        <div className="mx-auto flex max-w-[1120px] flex-col gap-4">
          {carregando || !nf ? (
            <EstadoDetalheNF carregando={carregando}>
              NF não encontrada.{" "}
              <Link to="/rh/notas" className="text-primary-text underline">
                Ver lista
              </Link>
            </EstadoDetalheNF>
          ) : (
            <>
              <CabecalhoNF nf={nf} voltar={VOLTAR} />

              <LayoutDetalheNF
                principal={
                  <>
                    <CamposNF nf={nf} />
                    <ArquivoNF nf={nf} />
                  </>
                }
                lateral={
                  <>
                    {nf.status === "Recusada" && nf.motivoRecusa && <MotivoRecusaNF nf={nf} />}

                    {temAcoes && (
                      <section className="flex flex-col gap-3 rounded-xl border bg-card p-[18px]">
                        <div className="flex flex-col gap-1">
                          <span className="text-[15px] font-semibold">{podeAnalisar ? "Análise do RH" : "Ações"}</span>
                          <span className="text-[13px] leading-snug text-muted-foreground">
                            {podeAprovar
                              ? "Ao aprovar, a NF será marcada como aprovada e ficará visível ao colaborador."
                              : podeAprovarManual
                                ? <>Esta NF está com status <strong>{nf.status}</strong> e não passou pela validação automática.</>
                                : "Reprocesse a leitura do PDF se os dados extraídos estiverem errados."}
                          </span>
                        </div>

                        {recusando ? (
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor="motivo" className="text-[13px]">Motivo *</Label>
                            <Textarea
                              id="motivo"
                              value={motivo}
                              onChange={(e) => setMotivo(e.target.value)}
                              rows={4}
                              autoFocus
                              placeholder="Ex.: Valor divergente do contrato, dados incorretos..."
                            />
                            <span className="text-xs text-muted-foreground">O motivo será visível ao colaborador.</span>
                            <div className="mt-1 flex justify-end gap-2">
                              <Button variant="outline" className="h-9" onClick={cancelarRecusa} disabled={loading}>
                                Cancelar
                              </Button>
                              <Button
                                variant="destructive"
                                className="h-9"
                                onClick={handleRecusar}
                                disabled={loading || !motivo.trim()}
                              >
                                Confirmar recusa
                              </Button>
                            </div>
                          </div>
                        ) : (
                          (podeRecusar || podeAprovar) && (
                            <div className={cn("grid gap-2", podeRecusar && podeAprovar && "grid-cols-2")}>
                              {podeRecusar && (
                                <Button
                                  variant="outline"
                                  className="border-destructive/40 text-destructive hover:bg-destructive/[0.07] hover:text-destructive"
                                  onClick={() => setRecusando(true)}
                                  disabled={loading}
                                >
                                  <XCircle /> Recusar
                                </Button>
                              )}
                              {podeAprovar && (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button className="bg-success text-success-foreground hover:bg-success/90" disabled={loading}>
                                      <CheckCircle2 /> Aprovar
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Aprovar esta NF?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Ao aprovar, a NF será marcada como aprovada e ficará visível ao colaborador.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                      <AlertDialogAction onClick={handleAprovar}>Confirmar aprovação</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              )}
                            </div>
                          )
                        )}

                        {!recusando && (podeAprovarManual || podeReprocessar) && (
                          <div className="flex flex-col gap-2 [&>button]:w-full">
                            {podeAprovarManual && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className="border-success/40 text-success hover:bg-success/10 hover:text-success"
                                    disabled={loading}
                                  >
                                    <CheckCircle2 /> Aprovar manualmente
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Aprovar manualmente?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Esta NF está com status <strong>{nf.status}</strong> e não passou pela validação automática.
                                      Ao aprovar manualmente, você confirma que verificou o documento.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                    <AlertDialogAction onClick={handleAprovarManual}>Confirmar aprovação manual</AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                            <ReprocessarNotaButton nf={nf} />
                          </div>
                        )}
                      </section>
                    )}

                    <HistoricoNF nf={nf} />
                  </>
                }
              />
            </>
          )}
        </div>
      </AppLayout>
    </RoleGuard>
  );
};

export default RHDetalheNF;
